import { useCallback, useEffect, useRef, useState } from "react";
import { fetchPersonasAll, deletePersona, setActiva, setBaja } from "../api/perfilesApi";

export function usePerfiles(initialPersonaId) {
    const [personas, setPersonas] = useState([]);
    const [error, setError] = useState(null);

    const [selectedPersona, setSelectedPersona] = useState(null);

    const [showAddPersona, setShowAddPersona] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [personaEditId, setPersonaEditId] = useState(null);

    const [showConfirm, setShowConfirm] = useState(false);
    const [infoModal, setInfoModal] = useState({ open: false, title: "", message: "" });

    const [refreshKey, setRefreshKey] = useState(0);
    const [showImageModal, setShowImageModal] = useState(false);

    const forceRefreshList = useCallback(() => setRefreshKey((k) => k + 1), []);

    const loadPersonas = useCallback(async () => {
        try {
            setError(null);
            const data = await fetchPersonasAll();
            setPersonas(data || []);
            if ((data || []).length > 0) {
                const preselect = initialPersonaId
                    ? data.find((p) => String(p.id) === String(initialPersonaId))
                    : null;
                setSelectedPersona(preselect || data[0]);
            }
        } catch (err) {
            console.error("Error al obtener personas:", err?.message || err);
            setError("No se pudo cargar la lista de perfiles.");
        }
    }, [initialPersonaId]);

    useEffect(() => {
        loadPersonas();
    }, [loadPersonas]);

    const handleEditarClick = useCallback((id) => {
        setPersonaEditId(id);
        setIsEditing(true);
        setShowAddPersona(false);
    }, []);

    // Al llegar con un id en la URL (ej. desde el calendario), abrir directamente en
    // edición una única vez, sin esperar a que cargue la lista completa: el propio
    // formulario (usePerfilLoad) ya pide esa persona individualmente.
    const autoEditConsumedRef = useRef(null);
    useEffect(() => {
        if (!initialPersonaId || autoEditConsumedRef.current === initialPersonaId) return;
        autoEditConsumedRef.current = initialPersonaId;
        handleEditarClick(initialPersonaId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialPersonaId]);

    const handleCerrarFormulario = useCallback(
        (personaActualizada) => {
            setIsEditing(false);
            setPersonaEditId(null);

            if (personaActualizada) {
                setPersonas((prev) => {
                    const idx = prev.findIndex((p) => p.id === personaActualizada.id);
                    if (idx === -1) return [...prev, personaActualizada];
                    const copy = [...prev];
                    copy[idx] = personaActualizada;
                    return copy;
                });
                setSelectedPersona(personaActualizada);
            }

            forceRefreshList();
        },
        [forceRefreshList]
    );

    const onPersonaCreada = useCallback(
        (p) => {
            setShowAddPersona(false);
            setPersonas((prev) => [...prev, p]);
            setSelectedPersona(p);
            forceRefreshList();
        },
        [forceRefreshList]
    );

    const handleEliminarPersona = useCallback(async () => {
        if (!selectedPersona?.id) return;

        try {
            const res = await deletePersona(selectedPersona.id);

            if (!res.ok) {
                let errorData = null;
                try {
                    errorData = await res.json();
                } catch {
                    // si no es JSON
                }

                if (errorData?.codigo === "PERSONA_NO_ENCONTRADA") {
                    setInfoModal({ open: true, title: "Persona no encontrada", message: errorData.mensaje });
                    setShowConfirm(false);
                    return;
                }

                if (errorData?.codigo === "PERSONA_ES_ADMINISTRADOR") {
                    setInfoModal({
                        open: true,
                        title: "No se puede eliminar",
                        message: `${errorData.mensaje}\n\nAdministrador: ${errorData.detalles}`,
                    });
                    setShowConfirm(false);
                    return;
                }

                if (errorData?.codigo === "PERSONA_EN_EVENTOS") {
                    const eventosLista = (errorData.detalles || []).map((e) => `  • ${e}`).join("\n");
                    setInfoModal({
                        open: true,
                        title: "Persona registrada en eventos",
                        message: `${errorData.mensaje}:\n\n${eventosLista}\n\nDebes eliminar la persona de estos eventos antes de continuar.`,
                    });
                    setShowConfirm(false);
                    return;
                }

                if (errorData?.codigo === "PERSONA_CON_ARMAS") {
                    const armasLista = (errorData.detalles || []).map((a) => `  • ${a}`).join("\n");
                    setInfoModal({
                        open: true,
                        title: "Persona con armas registradas",
                        message: `${errorData.mensaje}:\n\n${armasLista}\n\nDebes eliminar o reasignar estas armas antes de eliminar la persona.`,
                    });
                    setShowConfirm(false);
                    return;
                }

                setInfoModal({
                    open: true,
                    title: "Error al eliminar",
                    message: errorData?.mensaje || `Error ${res.status}: No se pudo eliminar la persona.`,
                });
                setShowConfirm(false);
                return;
            }

            // OK
            setPersonas((prev) => prev.filter((p) => p.id !== selectedPersona.id));

            setSelectedPersona((prevSelected) => {
                // recalcular con el array resultante
                const nextList = personas.filter((p) => p.id !== prevSelected.id);
                return nextList.length > 0 ? nextList[0] : null;
            });

            setShowConfirm(false);
            forceRefreshList();
        } catch (err) {
            console.error("Error eliminando persona:", err);
            setInfoModal({
                open: true,
                title: "Error de conexión",
                message: "No se pudo conectar con el servidor.",
            });
            setShowConfirm(false);
        }
    }, [selectedPersona, personas, forceRefreshList]);

    const toggleBajaActiva = useCallback(async () => {
        if (!selectedPersona?.id) return;

        try {
            const updated =
                selectedPersona.estado === "BAJA"
                    ? await setActiva(selectedPersona.id)
                    : await setBaja(selectedPersona.id);

            setSelectedPersona(updated);
            setPersonas((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
            forceRefreshList();
        } catch (e) {
            alert(
                selectedPersona.estado === "BAJA"
                    ? "No se pudo reactivar: " + e
                    : "No se pudo dar de baja: " + e
            );
        }
    }, [selectedPersona, forceRefreshList]);

    return {
        // data
        personas,
        error,
        selectedPersona,

        // UI state
        showAddPersona,
        isEditing,
        personaEditId,
        showConfirm,
        infoModal,
        refreshKey,
        showImageModal,

        // setters
        setSelectedPersona,
        setShowAddPersona,
        setIsEditing,
        setPersonaEditId,
        setShowConfirm,
        setInfoModal,
        setShowImageModal,

        // actions
        handleEditarClick,
        handleCerrarFormulario,
        handleEliminarPersona,
        onPersonaCreada,
        toggleBajaActiva,
    };
}
